/**
 * Shared constants for OTel trace span configuration.
 * Used by CustomTraceConfig (custom numeric trace metric).
 */

import { pickUnitOptions } from "./unitOptions"

export const KNOWN_SPAN_NAMES = [
  { key: "llm", label: "llm", description: "LLM inference spans" },
  { key: "tts", label: "tts", description: "Text-to-speech spans" },
  { key: "stt", label: "stt", description: "Speech-to-text spans" },
  {
    key: "llm_tool_call",
    label: "llm_tool_call",
    description: "LLM tool call spans",
  },
  {
    key: "tool_call",
    label: "tool_call",
    description: "Legacy tool call spans",
  },
]

export const KNOWN_ATTRIBUTES_BY_SPAN: Record<
  string,
  { key: string; label: string; description: string }[]
> = {
  llm: [
    {
      key: "metrics.ttfb",
      label: "metrics.ttfb",
      description: "Time to first token (seconds)",
    },
    {
      key: "gen_ai.usage.input_tokens",
      label: "gen_ai.usage.input_tokens",
      description: "Prompt token count",
    },
    {
      key: "gen_ai.usage.output_tokens",
      label: "gen_ai.usage.output_tokens",
      description: "Completion token count",
    },
  ],
  tts: [
    {
      key: "metrics.ttfb",
      label: "metrics.ttfb",
      description: "Time to first audio byte (seconds)",
    },
  ],
  stt: [
    {
      key: "metrics.ttfb",
      label: "metrics.ttfb",
      description: "Time to first transcript (seconds)",
    },
  ],
  llm_tool_call: [
    {
      key: "function.name",
      label: "function.name",
      description: "Name of the tool called",
    },
    {
      key: "tool_call_id",
      label: "tool_call_id",
      description: "Tool call identifier",
    },
    {
      key: "function.arguments",
      label: "function.arguments",
      description: "Tool call arguments",
    },
    {
      key: "tool.arguments",
      label: "tool.arguments",
      description: "Legacy tool call arguments",
    },
  ],
  tool_call: [
    {
      key: "function.name",
      label: "function.name",
      description: "Name of the tool called",
    },
    {
      key: "tool.name",
      label: "tool.name",
      description: "Legacy tool name",
    },
    {
      key: "tool_call_id",
      label: "tool_call_id",
      description: "Tool call identifier",
    },
    {
      key: "tool.call_id",
      label: "tool.call_id",
      description: "Legacy tool call identifier",
    },
    {
      key: "function.arguments",
      label: "function.arguments",
      description: "Tool call arguments",
    },
    {
      key: "tool.arguments",
      label: "tool.arguments",
      description: "Legacy tool call arguments",
    },
  ],
}

export const FALLBACK_ATTRIBUTES = [
  {
    key: "metrics.ttfb",
    label: "metrics.ttfb",
    description: "Time to first byte (seconds)",
  },
]

export const AGGREGATION_METHODS = [
  "average",
  "median",
  "p90",
  "p95",
  "p99",
  "max",
  "min",
  "sum",
  "count",
  "error_rate",
  "success_rate",
] as const

export type AggregationMethod = (typeof AGGREGATION_METHODS)[number]

export function isAggregationMethod(value: string): value is AggregationMethod {
  return (AGGREGATION_METHODS as readonly string[]).includes(value)
}

export const AGGREGATION_METHOD_OPTIONS: {
  key: AggregationMethod
  label: string
}[] = [
  { key: "average", label: "Average" },
  { key: "median", label: "Median" },
  { key: "p90", label: "P90 (90th percentile)" },
  { key: "p95", label: "P95 (95th percentile)" },
  { key: "p99", label: "P99 (99th percentile)" },
  { key: "max", label: "Maximum" },
  { key: "min", label: "Minimum" },
  { key: "sum", label: "Sum" },
  { key: "count", label: "Count" },
  { key: "error_rate", label: "Error Rate" },
  { key: "success_rate", label: "Success Rate" },
]

const NUMERIC_ATTRIBUTE_AGGREGATION_METHODS = [
  "average",
  "median",
  "p90",
  "p95",
  "p99",
  "max",
  "min",
  "sum",
] as const satisfies readonly AggregationMethod[]

export const SPAN_AGGREGATION_METHODS = [
  "count",
  "error_rate",
  "success_rate",
] as const satisfies readonly AggregationMethod[]

export function requiresNumericAttribute(method: AggregationMethod): boolean {
  return (NUMERIC_ATTRIBUTE_AGGREGATION_METHODS as readonly string[]).includes(
    method,
  )
}

// Drawn from the shared unit catalog so the entries overlapping with other
// numeric configs (Custom SQL, …) can't drift; "custom" adds the free-text mode.
export const CUSTOM_TRACE_UNIT_OPTIONS = pickUnitOptions([
  "none",
  "s",
  "ms",
  "count",
  "ratio",
  "percent",
  "tokens",
  "custom",
])

export type CustomTraceUnitOptionKey =
  (typeof CUSTOM_TRACE_UNIT_OPTIONS)[number]["key"]

/**
 * Resolves the effective unit for a custom trace metric, applying the
 * canonical fallback chain: `unit` → `unit_abbreviation` → `units`.
 * Centralizes the precedence so config + record renderers stay in sync.
 */
export function resolveCustomTraceUnit(
  metadata:
    | {
        unit?: string
        unit_abbreviation?: string
        units?: string
      }
    | undefined,
): string | undefined {
  return metadata?.unit ?? metadata?.unit_abbreviation ?? metadata?.units
}
